import { useState, useEffect } from 'react';
import { useTour } from '@/contexts/TourContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Clock, Sparkles } from 'lucide-react';

interface WelcomeModalProps {
  tourId: string;
  autoShow?: boolean;
}

export function WelcomeModal({ tourId, autoShow = true }: WelcomeModalProps) {
  const [open, setOpen] = useState(false);
  const { startTour, shouldShowTour, getTourState, updateTourProgress, isActive } = useTour();

  useEffect(() => {
    // Don't show welcome modal if a tour is already active
    if (isActive) {
      setOpen(false);
      return;
    }

    if (autoShow && shouldShowTour(tourId)) {
      // Show modal after a short delay for better UX
      const timer = setTimeout(() => {
        setOpen(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [tourId, autoShow, shouldShowTour, isActive]);

  const handleStartTour = () => {
    setOpen(false);
    startTour(tourId);
  };

  const handleDismiss = () => {
    setOpen(false);
    // Save dismissal state to localStorage
    const currentState = getTourState(tourId);
    updateTourProgress(tourId, {
      tourId,
      currentStep: 0,
      totalSteps: 0,
      status: 'dismissed',
      dismissalCount: (currentState?.dismissalCount || 0) + 1,
      skippedSteps: [],
    });
    console.log('[Tour] Dismissed permanently');
  };

  const handleRemindLater = () => {
    setOpen(false);
    // Save "remind later" state - will show again after 24 hours
    updateTourProgress(tourId, {
      tourId,
      currentStep: 0,
      totalSteps: 0,
      status: 'remind_later',
      dismissalCount: 0,
      skippedSteps: [],
    });
    console.log('[Tour] Remind me later - will show again in 24 hours');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-2xl">Welcome to TimeTrack Pro!</DialogTitle>
              <DialogDescription className="text-base mt-1">
                Let's get you started with a quick tour
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            We'll show you the key features to help you get the most out of TimeTrack Pro.
            This tour will only take about 60 seconds.
          </p>

          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
            <Clock className="h-4 w-4 shrink-0" />
            <span>Estimated time: 1 minute</span>
          </div>

          <div className="space-y-2 text-sm">
            <p className="font-medium">You'll learn about:</p>
            <ul className="space-y-1.5 ml-4">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Your personal dashboard and key metrics</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>How to navigate the application</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Quick entry for fast time logging</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Where to find help when you need it</span>
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={handleDismiss}
            className="w-full sm:w-auto"
          >
            Don't Show Again
          </Button>
          <Button
            variant="outline"
            onClick={handleRemindLater}
            className="w-full sm:w-auto"
          >
            Remind Me Later
          </Button>
          <Button
            onClick={handleStartTour}
            className="w-full sm:w-auto"
          >
            Start Tour
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
